/* eslint-disable max-lines */
import { ChangeDetectorRef, Component, ElementRef, Inject, ViewContainerRef } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import moment from 'moment-timezone';
import { Subject } from 'rxjs';
import { map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { UserSelectors } from '@recruitee/account-data';
import { AdminsSelectDialogComponent } from '@recruitee/account-smart-ui';
import type { CurrentUser, User } from '@recruitee/account-types';
import {
  CandidateSelectDropdownStrategy,
  QuickSearchStrategyService,
  CandidateSelectComponent,
} from '@recruitee/candidate';
import { FullCandidateActions, FullCandidateSelectors } from '@recruitee/candidate-data';
import { filterBoolean } from '@recruitee/common';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import { AppNavigatorService } from '@recruitee/navigation';
import type { CandidateSelectOption } from '@recruitee/search-types';
import { TaskErrorCode, TasksActions } from '@recruitee/tasks-data';
import type { Task } from '@recruitee/tasks-types';
import {
  DialogData,
  DialogRef,
  DialogService,
  DialogWidth,
  NotificationsController,
  RtStatefulComponent,
  ButtonLoadingDirective,
  DialogFooterComponent,
  TextareaComponent,
  TimePickerComponent,
  DropdownComponent,
  IconComponent,
  ButtonComponent,
  AdminsAvatarsComponent,
  RtTooltip,
  IconButtonComponent,
  DateComponent,
  CheckboxComponent,
  FormFieldComponent,
  LabelComponent,
  DividerComponent,
  InputComponent,
  DialogBodyComponent,
  DialogHeaderComponent,
  OverlayOriginDirective,
  ConnectedOverlayDirective,
} from '@recruitee/user-interface';
import { decodeHTML } from '@recruitee/utils';

type TimeData = { date: string; time: string; zone: string };

type State = {
  initialTaskData: Task;
  task: Task | any;
  candidate: CandidateSelectOption | null;
  editableCandidate: boolean;
  heading: string | null;
  selectedUsers: CurrentUser[] | null;
  timeData: TimeData | null;
  loading: boolean;
  isEditing: boolean;
  allowedUsers: User[];
  disallowedUsers: User[];
};

@Component({
  selector: 'rt-task-dialog',
  templateUrl: './task-dialog.component.html',
  styleUrls: ['./task-dialog.component.less'],
  providers: [
    {
      provide: CandidateSelectDropdownStrategy,
      useClass: QuickSearchStrategyService,
    },
  ],
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    ReactiveFormsModule,
    InputComponent,
    DividerComponent,
    LabelComponent,
    FormFieldComponent,
    CheckboxComponent,
    DateComponent,
    CandidateSelectComponent,
    IconButtonComponent,
    RtTooltip,
    AdminsAvatarsComponent,
    ButtonComponent,
    OverlayOriginDirective,
    IconComponent,
    ConnectedOverlayDirective,
    DropdownComponent,
    TimePickerComponent,
    TextareaComponent,
    DialogFooterComponent,
    ButtonLoadingDirective,
    TranslatePipe,
  ],
})
export class TaskDialogComponent extends RtStatefulComponent<State> {
  public readonly statusLabel: { completed: string; not_completed: string } = {
    completed: this.i18n.translate('app.tasks.completed'),
    not_completed: this.i18n.translate('app.tasks.not_completed'),
  };
  public taskForm: UntypedFormGroup;

  private ngOnDestroy$ = new Subject<void>();

  // eslint-disable-next-line max-lines-per-function
  constructor(
    private i18n: I18nService,
    private formBuilder: UntypedFormBuilder,
    private dialogService: DialogService,
    protected cdRef: ChangeDetectorRef,
    private element: ElementRef,
    private appNavigator: AppNavigatorService,
    private viewRef: ViewContainerRef,
    private dialog: DialogRef,
    private store: Store,
    private notificationsCtrl: NotificationsController,
    @Inject(DialogData)
    data: {
      task: Task;
      editableCandidate: boolean;
      allowedUsers: User[];
      disallowedUsers: User[];
      groupUsersByAccess?: boolean;
    },
    private actions$: Actions,
  ) {
    super(cdRef);
    this.createState({
      initialTaskData: data.task,
      task: data.task,
      candidate: null,
      editableCandidate: data.editableCandidate,
      heading: null,
      selectedUsers: null,
      timeData: null,
      loading: false,
      isEditing: false,
      allowedUsers: data.allowedUsers,
      disallowedUsers: data.disallowedUsers,
    });
    this.taskForm = this.formBuilder.group({
      title: [decodeHTML(this.state.task.title) || '', Validators.required],
      completed: this.state.task.completed || false,
      description: decodeHTML(this.state.task.description) || '',
    });
  }

  public ngOnInit(): void {
    if (this.state.task) {
      this.setState({
        ...this.state,
        heading: this.i18n.translate('app.tasks.edit_task'),
        isEditing: true,
        candidate: this.state.task.candidate,
        selectedUsers: this.state.task.admins || [],
      });

      this.setDate();
    } else {
      this.setState({
        ...this.state,
        heading: this.i18n.translate('app.tasks.create_task'),
        task: {},
      });
    }
    this.taskForm.valueChanges
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(() => this.cdRef.detectChanges());
    this.fetchCandidate();
    this.updateOnCandidateChange();
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public editAssignments(): void {
    const data = {
      assignments: (this.state.selectedUsers || []).map(admin => admin.id),
      ...(this.state.allowedUsers ? { allowedUsers: this.state.allowedUsers } : {}),
      ...(this.state.disallowedUsers ? { disallowedUsers: this.state.disallowedUsers } : {}),
    };
    const dialog = this.dialogService.custom(
      AdminsSelectDialogComponent,
      { width: DialogWidth.SmallX },
      data,
      this.viewRef,
    );

    dialog.afterClosed.subscribe(selectedUsers => {
      if (selectedUsers) {
        this.setState({
          ...this.state,
          selectedUsers,
        });
      }
    });
  }

  public onTimeChange(timeData: TimeData): void {
    this.setState({
      ...this.state,
      timeData: {
        ...timeData,
        time: moment(timeData.time, 'LT').format('HH:mm'),
      },
    });
  }

  public changeCandidate(candidate?: CandidateSelectOption): void {
    this.setState({ ...this.state, candidate: candidate || null });
    this.fetchCandidate();
  }

  public goToCandidate(): void {
    if (this.state.candidate) {
      this.appNavigator.openCandidateDialog(this.state.candidate.id);
    }
  }

  public getTimeDataString(): string | null {
    return this.state.timeData
      ? moment
          .tz(
            `${this.state.timeData.date}T${this.state.timeData.time}`,
            'YYYY-MM-DDTHH:mm',
            this.state.timeData.zone,
          )
          .format('ll LT')
      : null;
  }

  public cancel(): void {
    this.dialog.close();
  }

  public clearTimeData(): void {
    this.setState({
      ...this.state,
      timeData: null,
    });
  }

  public getDateToSubmit(): string | null {
    return this.state.timeData
      ? moment
          .tz(
            `${this.state.timeData.date}T${this.state.timeData.time}`,
            'YYYY-MM-DDTHH:mm',
            this.state.timeData.zone,
          )
          .utc()
          .format()
      : null;
  }

  public submit(): void {
    const task = {
      id: this.state.task ? this.state.task.id : null,
      title: this.taskForm.value.title,
      completed: !!this.taskForm.value.completed,
      description: this.taskForm.value.description,
      dueDate: this.getDateToSubmit(),
      timezone: this.state.timeData ? this.state.timeData.zone : null,
      candidateId: this.state.candidate ? this.state.candidate.id : null,
      adminIds: (this.state.selectedUsers || []).map(admin => admin.id),
    };
    this.setState({
      ...this.state,
      loading: true,
    });

    this.store.dispatch(TasksActions.updateTask({ payload: { data: task } }));

    this.actions$
      .pipe(ofType(TasksActions.updateTaskSuccess), take(1), takeUntil(this.ngOnDestroy$))
      .subscribe(() => {
        this.setState({
          ...this.state,
          loading: false,
        });
        this.dialog.close(true);
      });

    this.actions$.pipe(ofType(TasksActions.updateTaskFailed), take(1)).subscribe(({ payload }) => {
      const error = payload.error.errors[0];
      if (error.code === TaskErrorCode.CannotAssignAdminWithoutAccess) {
        this.notificationsCtrl.error(error.message);
      }
      this.setState({
        ...this.state,
        loading: false,
      });
    });
  }

  public delete(): void {
    this.dialogService
      .error(
        {
          title: this.i18n.translate('app.tasks.delete_confirmation.title'),
          message: this.i18n.translate('app.tasks.delete_confirmation.message'),
          agreeLabel: this.i18n.translate('app.tasks.delete_confirmation.agree_label'),
        },
        this.viewRef,
      )
      .pipe(
        take(1),
        filterBoolean,
        tap(() =>
          this.store.dispatch(
            TasksActions.removeOnePessimistic({ payload: { data: this.state.task.id } }),
          ),
        ),
        switchMap(() =>
          this.actions$.pipe(ofType(TasksActions.removeOnePessimisticSuccess), take(1)),
        ),
      )
      .subscribe(() => {
        this.dialog.close(true);
      });
  }

  private setDate(): void {
    if (this.state.task.dueDate) {
      const momentDate = moment.tz(this.state.task.dueDate, this.state.task.timezone);

      this.setState({
        ...this.state,
        timeData: {
          date: momentDate.format('YYYY-MM-DD'),
          time: momentDate.format('HH:mm'),
          zone: this.state.task.timezone,
        },
      });
    }
  }

  private fetchCandidate(): void {
    if (!this.state.candidate) {
      return;
    }

    this.store.dispatch(
      FullCandidateActions.fetchOne({ payload: { id: this.state.candidate.id } }),
    );
  }

  private resetSelectedUsers(allowedUsers: User[]): void {
    const newUsers =
      this.state.selectedUsers?.filter(user =>
        allowedUsers.find(allowedUser => allowedUser.id === user.id),
      ) ?? [];
    this.setState({
      ...this.state,
      selectedUsers: newUsers,
    });
  }

  private updateOnCandidateChange(): void {
    this.state$
      .pipe(
        switchMap(({ candidate }) =>
          candidate?.id
            ? this.store.select(FullCandidateSelectors.commonUsersGrouped(candidate.id))
            : this.store
                .select(UserSelectors.entities())
                .pipe(map(users => ({ usersWithAccess: users, usersWithoutAccess: [] }))),
        ),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(({ usersWithAccess, usersWithoutAccess }) => {
        this.resetSelectedUsers(usersWithAccess);
        this.setState({
          ...this.state,
          allowedUsers: usersWithAccess,
          disallowedUsers: usersWithoutAccess,
        });
      });
  }
}
